import type { ApiClient } from '@carbonwebmail/api-client'
import type { OrgPlanLimitsDto } from '@carbonwebmail/contracts'
import { Async } from '../../ui/Async.js'
import { usePlan } from './usePlan.js'

/**
 * What this organisation is allowed, and how much of it is gone.
 *
 * **Shown rather than only enforced.** An allowance somebody meets for the
 * first time by being refused reads as a fault in the product: "your plan has
 * no room for another person" means nothing to somebody who was never told
 * there was a plan. The invitation that cannot be sent is the one people reach
 * first, so seats are listed first.
 *
 * **There is nothing to press.** Plans are assigned rather than bought —
 * there is no billing here, so an upgrade button would have to invent a price
 * and a payment flow. The honest screen says who to ask.
 */

const number = (value: number) => value.toLocaleString('en-GB')

export function PlanPage({ client }: { readonly client: ApiClient }) {
  const { state, retry } = usePlan({ client })

  return (
    <>
      <header className="mb-6">
        <p className="mt-1 text-[13px] text-t2">
          What your organisation is allowed, and what it has used. Your plan is set by us — tell us
          what you need and we will move you.
        </p>
      </header>

      <Async
        state={state}
        skeleton={<p className="text-[13px] text-t3">Loading…</p>}
        onRetry={() => void retry()}
      >
        {(plan) => (
          <section className="max-w-2xl rounded-card border border-line bg-surface p-5">
            <h2 className="mb-1 font-bold text-[15px] text-t1 capitalize">{plan.plan}</h2>
            <p className="mb-5 text-[13px] text-t2">
              Sends and AI tokens are counted per calendar month and start again on the first.
            </p>

            <dl className="grid gap-4 sm:grid-cols-2">
              <Allowance
                label="People"
                used={plan.usage.seats}
                limit={plan.limits.seats}
                note="Members plus invitations nobody has accepted yet."
              />
              <Allowance
                label="Contacts"
                used={plan.usage.contacts}
                limit={plan.limits.contacts}
                note="Stored right now, across every segment."
              />
              <Allowance
                label="Sends this month"
                used={plan.usage.sendsThisMonth}
                limit={plan.limits.sendsPerMonth}
                note="Messages a provider accepted."
              />
              <Allowance
                label="AI tokens this month"
                used={plan.usage.aiTokensThisMonth}
                limit={plan.limits.aiTokensPerMonth}
                note="What the designer and the writing help have asked for."
              />
            </dl>
          </section>
        )}
      </Async>
    </>
  )
}

/**
 * One allowance.
 *
 * A bar is drawn only when there is something to draw it against. An uncapped
 * plan with a full-width bar would say the opposite of what is true, and an
 * empty one would look like nothing had been used.
 */
function Allowance({
  label,
  used,
  limit,
  note,
}: {
  readonly label: string
  readonly used: number
  readonly limit: OrgPlanLimitsDto['limits']['seats']
  readonly note: string
}) {
  const share = limit === null || limit === 0 ? undefined : Math.min(1, used / limit)

  return (
    <div>
      <dt className="font-medium text-[13px] text-t1">{label}</dt>
      <dd className="mt-1">
        <p className="text-[13px] text-t2">
          {number(used)}
          {limit === null ? ' — no limit on your plan' : ` of ${number(limit)}`}
        </p>
        {share === undefined ? null : (
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-line">
            <div
              // Red at the line rather than near it: a bar that turned red at
              // eighty per cent would be telling somebody they had a problem
              // when they had a fifth of their allowance left.
              className={`h-full ${share >= 1 ? 'bg-danger' : 'bg-brand'} ${WIDTHS[tenth(share)]}`}
            />
          </div>
        )}
        <p className="mt-1 text-[12px] text-t3">{note}</p>
      </dd>
    </div>
  )
}

/**
 * A tenth of the track per step, with something visible for a little.
 *
 * Rounded up off zero: an organisation that has used three contacts out of
 * five hundred has used some, and a bar of nothing says it has not started.
 */
function tenth(share: number): number {
  const steps = Math.round(share * 10)
  return steps === 0 && share > 0 ? 1 : steps
}

/**
 * Literal classes, never assembled.
 *
 * Tailwind generates classes by scanning source text, so `w-[${n}%]` is a
 * class that does not exist in the stylesheet and silently renders at zero —
 * the same trap `BarChart`'s heights document.
 */
const WIDTHS: Record<number, string> = {
  0: 'w-0',
  1: 'w-[10%]',
  2: 'w-[20%]',
  3: 'w-[30%]',
  4: 'w-[40%]',
  5: 'w-[50%]',
  6: 'w-[60%]',
  7: 'w-[70%]',
  8: 'w-[80%]',
  9: 'w-[90%]',
  10: 'w-full',
}
